/// <amd-module name="Repos/Common/ReposActionService" />
import { ICreateBranchDialogProps, ICreateTagDialogProps, IDeleteBranchDialogProps, IDeleteTagDialogProps } from "Repos/VersionDialogs/Dialogs.Props";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { GitRefUpdateResult } from "./Contracts/Git";
import { IReposRoutingService } from "./ReposRoutingService";
import { refToBranchName } from "./Util/Ref";
import { branchToVersionDescriptor } from "./Util/Version";
import { getGitRepository } from "./VCViewModelDataProviderService";

export interface IReposActionService extends IVssService {
    createBranch: (props?: ICreateBranchDialogProps) => void;
    createTag: (props?: ICreateTagDialogProps) => void;
    deleteBranch: (props?: IDeleteBranchDialogProps) => void;
    deleteTag: (props?: IDeleteTagDialogProps) => void;
}

class ReposActionService extends VssService implements IReposActionService {
    public createBranch(props?: ICreateBranchDialogProps): void {
        this.openVersionDialog("createBranch", props);
    }

    public createTag(props?: ICreateTagDialogProps): void {
        this.openVersionDialog("createTag", props);
    }

    public deleteBranch(props?: IDeleteBranchDialogProps): void {
        this.openVersionDialog("deleteBranch", props);
    }

    public deleteTag(props?: IDeleteTagDialogProps): void {
        this.openVersionDialog("deleteTag", props);
    }

    private openVersionDialog<T>(dialogName: string, props?: T): void {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        contributionService.executeCommandEx(
            "ms.vss-code-web.tags-version-dialog-actions-command",
            {
                dependencies: ["ms.vss-code-web.version-dialogs-content"],
                serviceName: "IVersionDialogActionsService",
                methodName: dialogName
            },
            props
        );
    }
}

const serviceName = "IReposActionService";
Services.add(serviceName, { serviceFactory: ReposActionService });

function getService(pageContext: IVssPageContext): IReposActionService {
    return pageContext.getService<IReposActionService>(serviceName);
}

/**
 * Opens create branch dialog using the specified props.
 *
 * @param pageContext Page context.
 * @param props Create branch dialog props.
 */
export function createBranch(pageContext: IVssPageContext, props?: ICreateBranchDialogProps): void {
    getService(pageContext).createBranch(props);
}

/**
 * Opens create tag dialog using the specified props.
 *
 * @param pageContext Page context.
 * @param props Create tag dialog props.
 */
export function createTag(pageContext: IVssPageContext, props?: ICreateTagDialogProps): void {
    getService(pageContext).createTag(props);
}

/**
 * Opens delete branch dialog using the specified props.
 *
 * @param pageContext Page context.
 * @param props Delete branch dialog props.
 */
export function deleteBranch(pageContext: IVssPageContext, props?: IDeleteBranchDialogProps): void {
    getService(pageContext).deleteBranch(props);
}

/**
 * Opens delete tag dialog using the specified props.
 *
 * @param pageContext Page context.
 * @param props Delete tag dialog props.
 */
export function deleteTag(pageContext: IVssPageContext, props?: IDeleteTagDialogProps): void {
    getService(pageContext).deleteTag(props);
}

/**
 * Gets the default create branch action which basically opens create branch dialog with:
 *     - Empty branch name
 *     - Master branch is selected as base
 *
 * And makes and FPS call File explorer view with the newly created branch selected when
 * create branch operation completes successfully.
 *
 * @param pageContext Page context.
 * @param props Extra props to pass to default branch action.
 * @param path Path to the item to navigate to after the branch is created.
 */
export function getDefaultCreateBranchAction(pageContext: IVssPageContext, props?: ICreateBranchDialogProps, path?: string): () => void {
    return () => {
        const repository = getGitRepository(pageContext);
        createBranch(pageContext, {
            ...props,
            onComplete: (newBranch: GitRefUpdateResult) => {
                // Navigate to branch page
                const routingService = pageContext.getService<IReposRoutingService>("IReposRoutingService");
                const branchUrl = routingService.getFilesUrl(
                    repository!.name,
                    repository!.project.name,
                    path,
                    branchToVersionDescriptor(refToBranchName(newBranch.name))
                );

                FastPageSwitch(pageContext, branchUrl, true);
            },
            repository
        } as ICreateBranchDialogProps);
    };
}
